import React from "react";
import { CardContent, Typography, Grid, Chip, ListItemText, ListItem, List, Box, TextField } from "@mui/material";
import { format } from "date-fns";
import ManagerPocketMoneyRecord from "../pocket-money-record";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const SelectedTaskDetailsManager = ({ selectedTask }) => {
  if (!selectedTask) return null;

  const formatTime = (dateString) => {
    const date = new Date(dateString);

    // Extract hours, minutes, and seconds
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const seconds = String(date.getUTCSeconds()).padStart(2, "0");

    // Format as "HH : MM : SS"
    return `${hours} : ${minutes} : ${seconds}`;
  };
  const isCompleted =
    selectedTask?.completedBy !== "" && selectedTask?.completed_by !== "" && selectedTask?.task?.dropoff_time !== null;

  return (
    <>
      <CardContent>
        {/* Task Title */}
        <Typography sx={{ fontWeight: "bold", fontSize: "16px", color: "black", mb: 1 }}>
          {selectedTask.taskName || selectedTask?.planned_appointments}
        </Typography>

        {/* Task Status */}
        <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
          <Grid item lg={6} md={6} sm={6} xs={12}>
            <Typography sx={{ fontSize: "14px" }}>Status</Typography>
          </Grid>
          <Grid item lg={6} md={6} sm={6} xs={12}>
            <Chip
              label={isCompleted ? "Completed" : "Pending"}
              sx={{
                fontSize: "12px",
                backgroundColor: isCompleted ? "#EAFFF2" : "#FFF8E5",
                color: isCompleted ? "green" : "orange",
              }}
            />
          </Grid>
        </Grid>

        {/* Shift */}
        <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
          <Grid item lg={6} md={6} sm={6} xs={12}>
            <Typography sx={{ fontSize: "14px" }}>Shift</Typography>
          </Grid>
          <Grid item lg={6} md={6} sm={6} xs={12}>
            <Typography variant='body2'>{selectedTask?.mainTask?.shiftType}</Typography>
          </Grid>
        </Grid>

        {/* Date Completed */}
        <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
          <Grid item lg={6} md={6} sm={6} xs={12}>
            <Typography sx={{ fontSize: "14px" }}>Date Completed</Typography>
          </Grid>
          <Grid item lg={6} md={6} sm={6} xs={12}>
            <Typography variant='body2'>
              {isCompleted
                ? format(
                    new Date(
                      selectedTask?.updatedAt ||
                        selectedTask?.task?.date ||
                        selectedTask?.created_at ||
                        selectedTask?.date
                    ),
                    "dd MMM yyyy"
                  )
                : "Not completed"}
            </Typography>
          </Grid>
        </Grid>

        {/* Time Completed */}
        <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
          <Grid item lg={6} md={6} sm={6} xs={12}>
            <Typography sx={{ fontSize: "14px" }}>Time</Typography>
          </Grid>
          <Grid item lg={6} md={6} sm={6} xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccessTimeIcon fontSize='small' />
              <Typography variant='body2'>
                {isCompleted
                  ? formatTime(
                      selectedTask?.updatedAt ||
                        selectedTask?.task?.date ||
                        selectedTask?.created_at ||
                        selectedTask?.date
                    )
                  : "Not completed"}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Key Category */}
        {selectedTask?.task?.key_category && (
          <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
            <Grid item lg={6} md={6} sm={6} xs={12}>
              <Typography sx={{ fontSize: "14px" }}>Key Category</Typography>
            </Grid>
            <Grid item lg={6} md={6} sm={6} xs={12}>
              <Typography variant='body2'>{selectedTask?.task?.key_category}</Typography>
            </Grid>
          </Grid>
        )}

        {/* Pickup Time */}
        {selectedTask?.task?.pickup_time && (
          <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
            <Grid item lg={6} md={6} sm={6} xs={12}>
              <Typography sx={{ fontSize: "14px" }}>Sign In</Typography>
            </Grid>
            <Grid item lg={6} md={6} sm={6} xs={12}>
              <Typography variant='body2'>{selectedTask?.task?.pickup_time}</Typography>
            </Grid>
          </Grid>
        )}

        {/* DropOff Time */}
        {selectedTask?.task?.dropoff_time && (
          <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
            <Grid item lg={6} md={6} sm={6} xs={12}>
              <Typography sx={{ fontSize: "14px" }}>Sign Out</Typography>
            </Grid>
            <Grid item lg={6} md={6} sm={6} xs={12}>
              <Typography variant='body2'>{selectedTask?.task?.dropoff_time}</Typography>
            </Grid>
          </Grid>
        )}

        {/* Assignee */}
        <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
          <Grid item lg={6} md={6} sm={6} xs={12}>
            <Typography sx={{ fontSize: "14px" }}>Assignees</Typography>
          </Grid>
          <Grid item lg={6} md={6} sm={6} xs={12}>
            <Typography variant='body2'>{selectedTask?.mainTask?.assignee || "Not Assigned Yet"}</Typography>
          </Grid>
        </Grid>

        {selectedTask.taskName === "Pocket Money Record" && <ManagerPocketMoneyRecord selectedTask={selectedTask} />}

        {/* Questions */}
        {selectedTask?.question && (
          <Grid container spacing={1} alignItems='center' sx={{ mb: 2, mt: 4 }}>
            <Grid item lg={11} md={11} sm={9} xs={12}>
              <Typography sx={{ fontSize: "14px" }}>{selectedTask?.question}</Typography>
            </Grid>
            <Grid item lg={1} md={1} sm={3} xs={12}>
              <Typography variant='body2' color={selectedTask?.descale_status === "Yes" ? "blue" : "red"}>
                {selectedTask?.descale_status}
              </Typography>
            </Grid>
          </Grid>
        )}

        {/* Comments */}
        {selectedTask?.reason && (
          <>
            <Typography sx={{ fontSize: "14px", mb: 1 }}>Comments: </Typography>
            <TextField
              variant='outlined'
              size='small'
              value={selectedTask?.reason}
              sx={{
                width: "100%",
                mb: 1, // Ensure it spans full width
              }}
              multiline
              rows={2}
              disabled
            />
          </>
        )}

        {/* Fridge Temperature  */}
        {selectedTask?.fridge_temp && (
          <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
            <Grid item lg={10} md={6} sm={6} xs={12}>
              <Typography sx={{ fontSize: "14px" }}>Fridge Temperature</Typography>
            </Grid>
            <Grid item lg={2} md={6} sm={6} xs={12}>
              <Typography sx={{ fontSize: "14px" }}>{selectedTask?.fridge_temp} C</Typography>
            </Grid>
          </Grid>
        )}

        {/* Freezer Temperature  */}
        {selectedTask?.freezer_temp && (
          <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
            <Grid item lg={10} md={6} sm={6} xs={12}>
              <Typography sx={{ fontSize: "14px" }}>Freezer Temperature</Typography>
            </Grid>
            <Grid item lg={2} md={6} sm={6} xs={12}>
              <Typography sx={{ fontSize: "14px" }}>{selectedTask?.freezer_temp} C</Typography>
            </Grid>
          </Grid>
        )}

        {/* Comments */}
        {selectedTask?.comments && (
          <>
            <Typography sx={{ fontSize: "14px", mb: 1 }}>Comments: </Typography>
            <TextField
              variant='outlined'
              size='small'
              value={selectedTask?.comments}
              sx={{
                width: "100%",
                mb: 1, // Ensure it spans full width
              }}
              multiline
              rows={2}
              disabled
            />
          </>
        )}

        {/* Area Temperature Taken In */}
        {selectedTask?.area && (
          <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
            <Grid item lg={8} md={6} sm={6} xs={12}>
              <Typography sx={{ fontSize: "14px" }}>Area Temperature Taken In </Typography>
            </Grid>
            <Grid item lg={4} md={6} sm={6} xs={12}>
              <Typography sx={{ fontSize: "14px" }}>{selectedTask?.area}</Typography>
            </Grid>
          </Grid>
        )}

        {/* Hot Temperature  */}
        {selectedTask?.hot_temp && (
          <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
            <Grid item lg={10} md={6} sm={6} xs={12}>
              <Typography sx={{ fontSize: "14px" }}>Hot Temperature</Typography>
            </Grid>
            <Grid item lg={2} md={6} sm={6} xs={12}>
              <Typography sx={{ fontSize: "14px" }}>{selectedTask?.hot_temp} C</Typography>
            </Grid>
          </Grid>
        )}

        {/* Cold Temperature  */}
        {selectedTask?.cold_temp && (
          <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
            <Grid item lg={10} md={6} sm={6} xs={12}>
              <Typography sx={{ fontSize: "14px" }}>Cold Temperature</Typography>
            </Grid>
            <Grid item lg={2} md={6} sm={6} xs={12}>
              <Typography sx={{ fontSize: "14px" }}>{selectedTask?.cold_temp} C</Typography>
            </Grid>
          </Grid>
        )}

        {/*  Actions */}
        {(selectedTask?.hot_action || selectedTask?.cold_action) && (
          <Typography sx={{ fontSize: "14px", mb: 1 }}> Actions:</Typography>
        )}

        {/* Hot Action */}
        {selectedTask?.hot_action && (
          <>
            <Typography sx={{ fontSize: "14px", mb: 1 }}>Hot Action</Typography>
            <TextField
              variant='outlined'
              size='small'
              value={selectedTask?.hot_action}
              sx={{
                width: "100%",
                mb: 1, // Ensure it spans full width
              }}
              multiline
              rows={2}
              disabled
            />
          </>
        )}

        {/* Cold Action */}
        {selectedTask?.cold_action && (
          <>
            <Typography sx={{ fontSize: "14px", mb: 1 }}>Cold Action</Typography>
            <TextField
              variant='outlined'
              size='small'
              value={selectedTask?.cold_action}
              sx={{
                width: "100%",
                mb: 1, // Ensure it spans full width
              }}
              multiline
              rows={2}
              disabled
            />
          </>
        )}

        {/* Planned Activity */}
        {selectedTask?.planned_activity && (
          <>
            <Typography sx={{ fontSize: "14px", mb: 1 }}>Planned Activity </Typography>
            <TextField
              variant='outlined'
              size='small'
              value={selectedTask?.planned_activity}
              sx={{
                width: "100%",
                mb: 1, // Ensure it spans full width
              }}
              multiline
              rows={2}
              disabled
            />
          </>
        )}

        {/* Actual Appointment Attend  */}
        {selectedTask?.actual_appointments && (
          <>
            <Typography sx={{ fontSize: "14px", mb: 1 }}>Actual Appointment Attend </Typography>
            <TextField
              variant='outlined'
              size='small'
              value={selectedTask?.actual_appointments}
              sx={{
                width: "100%",
                mb: 1, // Ensure it spans full width
              }}
              multiline
              rows={2}
              disabled
            />
          </>
        )}

        {/* Comments */}
        {selectedTask?.comment && (
          <>
            <Typography sx={{ fontSize: "14px", mb: 1 }}>Comments </Typography>
            <TextField
              variant='outlined'
              size='small'
              value={selectedTask?.comment}
              sx={{
                width: "100%",
                mb: 1, // Ensure it spans full width
              }}
              multiline
              rows={2}
              disabled
            />
          </>
        )}

        {/* Questions */}
        {selectedTask?.selectedItem?.question && (
          <Grid container spacing={1} alignItems='center' sx={{ mb: 2, mt: 4 }}>
            <Grid item lg={11} md={11} sm={9} xs={12}>
              <Typography sx={{ fontSize: "14px" }}>{selectedTask?.selectedItem?.question}</Typography>
            </Grid>
            <Grid item lg={1} md={1} sm={3} xs={12}>
              <Typography variant='body2' color={selectedTask?.selectedItem?.condition === "Yes" ? "blue" : "red"}>
                {selectedTask?.selectedItem?.condition}
              </Typography>
            </Grid>
          </Grid>
        )}

        {/* Product Name */}
        {selectedTask?.selectedItem?.product && (
          <Box mt={4}>
            <Typography sx={{ fontSize: "14px", mb: 1 }}>Product Name :</Typography>
            <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
              <Grid item lg={11} md={11} sm={9} xs={12}>
                <Typography sx={{ fontSize: "14px" }}>{selectedTask?.selectedItem?.product}</Typography>
              </Grid>
              <Grid item lg={1} md={1} sm={3} xs={12}>
                <Typography sx={{ fontSize: "14px", color: "blue" }}>{selectedTask?.selectedItem?.amount}</Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Quantity */}
        {selectedTask?.selectedItem?.quantity && (
          <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
            <Grid item lg={11} md={11} sm={9} xs={12}>
              <Typography sx={{ fontSize: "14px" }}>Quantity :</Typography>
            </Grid>
            <Grid item lg={1} md={1} sm={3} xs={12}>
              <Typography sx={{ fontSize: "14px", color: "blue" }}>{selectedTask?.selectedItem?.quantity}</Typography>
            </Grid>
          </Grid>
        )}

        {/* Comments / Action Taken: */}
        {selectedTask?.selectedItem?.comments && (
          <>
            <Typography sx={{ fontSize: "14px", mb: 1 }}>Comments:</Typography>
            <TextField
              variant='outlined'
              size='small'
              value={selectedTask?.selectedItem?.comments}
              sx={{
                width: "100%", // Ensure it spans full width
              }}
              multiline
              rows={2}
              disabled
            />
          </>
        )}

        {/* Damages */}
        {selectedTask?.selectedItem?.damages && (
          <>
            <Typography sx={{ fontSize: "14px", mb: 1 }}>External / Interior Damage :</Typography>
            <TextField
              variant='outlined'
              size='small'
              value={selectedTask?.selectedItem?.damages}
              sx={{
                width: "100%", // Ensure it spans full width
              }}
              multiline
              rows={2}
              disabled
            />
          </>
        )}

        {/* Description */}
        {selectedTask?.description && (
          <>
            <Typography sx={{ fontSize: "14px", mb: 1 }}>Description</Typography>
            <TextField
              variant='outlined'
              size='small'
              value={selectedTask?.description}
              sx={{
                width: "100%", // Ensure it spans full width
              }}
              multiline
              rows={2}
              disabled
            />
          </>
        )}
        {selectedTask?.checklist && selectedTask?.checklist?.length > 0 && (
          <Box sx={{ padding: "16px", borderRadius: "8px" }}>
            {/* Header */}
            <Grid container>
              <Grid item lg={9} md={9} sm={9} xs={12}>
                <Typography sx={{ fontSize: "14px", color: "black" }}>Sub tasks</Typography>
              </Grid>
              <Grid item lg={3} md={3} sm={3} xs={12}>
                <Typography sx={{ fontSize: "14px" }}>{`${
                  selectedTask.checklist ? selectedTask.checklist.length : 0
                } tasks`}</Typography>
              </Grid>
            </Grid>

            {/* Subtasks */}

            <List>
              {selectedTask.checklist.map((subtask, index) => (
                <ListItem
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 0",
                  }}
                >
                  <ListItemText
                    primary={subtask.item}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: "12px",
                        color: "black",
                        textDecoration: subtask.completed ? "line-through" : "none",
                      },
                    }}
                  />
                  <Typography sx={{ fontSize: "12px", color: "black" }}>
                    {selectedTask.updated_by ?? "Not Completed"}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </CardContent>
    </>
  );
};

export default SelectedTaskDetailsManager;
